import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import useDebouncedValue from '@/hooks/useDebouncedValue';

/**
 * Such- und Filterleiste der Admin-Listen.
 *
 * Die Wahrheit steht in den URL-Parametern (teilbar, überlebt Reload und
 * Zurück-Button) — diese Komponente hält nur den Tastatur-Zwischenstand des
 * Suchfelds und meldet ihn entdebounced nach oben.
 *
 * @param {string}   search           aktueller Suchbegriff aus der URL
 * @param {Function} onSearchChange   erhält den entdebouncten Begriff
 * @param {Array}    filters          [{ key, label, value, options, onChange }]
 * @param {Function} onReset          optional: alle Filter zurücksetzen
 */
const AdminListToolbar = ({
  search = '',
  onSearchChange,
  searchPlaceholder = 'Suchen …',
  searchLabel = 'Suche',
  filters = [],
  onReset,
  children,
}) => {
  const [term, setTerm] = useState(search);
  const debouncedTerm = useDebouncedValue(term, 300);

  // Zuletzt an die URL gemeldeter Wert. Ohne diesen Vergleich schiebt der
  // Effekt den Anfangswert direkt wieder nach oben (und beim Zurück-Button
  // schaukeln sich Feld und URL gegenseitig auf).
  const committedRef = useRef(search);
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  // URL → Feld (Zurück-Button, „Filter zurücksetzen").
  useEffect(() => {
    if (search !== committedRef.current) {
      committedRef.current = search;
      setTerm(search);
    }
  }, [search]);

  // Feld → URL.
  useEffect(() => {
    if (debouncedTerm === committedRef.current) return;
    committedRef.current = debouncedTerm;
    onSearchChangeRef.current?.(debouncedTerm);
  }, [debouncedTerm]);

  const hasActiveFilters =
    Boolean(search) || filters.some((filter) => Boolean(filter.value));

  return (
    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <div className="sm:max-w-xs sm:flex-1">
        <label
          htmlFor="admin-list-search"
          className="block text-sm font-medium text-gray-900"
        >
          {searchLabel}
        </label>
        <div className="relative mt-1">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            id="admin-list-search"
            type="search"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={searchPlaceholder}
            className="block w-full rounded-md border-0 py-1.5 pl-9 pr-9 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
          />
          {term && (
            <button
              type="button"
              onClick={() => setTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 hover:cursor-pointer"
            >
              <span className="sr-only">Suche leeren</span>
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {filters.map((filter) => (
        <div key={filter.key} className="sm:w-48">
          <label
            htmlFor={`admin-filter-${filter.key}`}
            className="block text-sm font-medium text-gray-900"
          >
            {filter.label}
          </label>
          <select
            id={`admin-filter-${filter.key}`}
            value={filter.value ?? ''}
            onChange={(event) => filter.onChange(event.target.value)}
            className="mt-1 block w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-8 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
          >
            {filter.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ))}

      {children}

      {hasActiveFilters && onReset && (
        <button
          type="button"
          onClick={onReset}
          className="self-start rounded-md px-3 py-1.5 text-sm font-semibold text-svisit-primary-600 hover:bg-svisit-primary-50 hover:cursor-pointer sm:self-auto"
        >
          Filter zurücksetzen
        </button>
      )}
    </div>
  );
};

export default AdminListToolbar;
